import React from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { message, Modal, Typography } from 'antd';
import { ActionButton } from '@doer/frontend-components/buttons';
import { Spinner } from '@doer/frontend-components/spinner';
import { Pencil, Trash2 } from 'lucide-react';
import { CreativityContentData } from '../../../../types/banner';
import { AvatarContent } from '../../../../types/content';
import { getBannerStatusData } from '../../../../utils';
import { useResponsiveSizes } from '../../../../hooks/use-responsive-sizes';
import { useDeleteBanner } from '../../../../hooks/banners/use-delete-banner';
import { getTemplateByBannerType } from '../../../../clients/registry';
import { tokens } from '../../../../theme/tokens';

dayjs.extend(relativeTime);
dayjs.locale('es');

type LibraryAsset = CreativityContentData | (AvatarContent & { bannerId: string });

interface GeneralInfoCardProps {
  creativity: LibraryAsset;
  onDeleted?: () => void;
  onClose: () => void;
}

export const GeneralInfoCard: React.FC<GeneralInfoCardProps> = ({ creativity, onDeleted, onClose }) => {
  const { fontSize, spacing, screens } = useResponsiveSizes();
  const { deleteBanner } = useDeleteBanner();
  const navigate = useNavigate();

  const [isDeleting, setIsDeleting] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  const statusData = getBannerStatusData(creativity);

  // Solo se reabre lo que tiene un modelo guardado, y cada producto tiene su editor.
  const editorRoute = React.useMemo((): string | null => {
    if (creativity.type === 'avatar') {
      return creativity.editorModel ? `/avatars-editor/${creativity.projectId}` : null;
    }
    const bannerType = creativity.bannerType ?? creativity.creativityType;
    const baseTemplateId = bannerType ? getTemplateByBannerType(bannerType)?.id : undefined;
    if (!creativity.editorModels?.length || !baseTemplateId) return null;
    return `/creativity-editor/${encodeURIComponent(baseTemplateId)}/${creativity.bannerId}`;
  }, [creativity]);

  const handleEdit = () => {
    if (!editorRoute) return;
    navigate(editorRoute);
    onClose();
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const res = await deleteBanner(creativity.bannerId);
      if (res) {
        message.success('Asset eliminado correctamente');
        onDeleted?.();
        onClose();
      } else {
        message.error('No se pudo eliminar');
      }
    } catch (error) {
      message.error('No se pudo eliminar');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const titleStyle = {
    color: tokens.colors.primary.neutral[1000],
    fontWeight: 500
  };

  const infoStyle = { color: '#0f172abb', fontWeight: 400 };

  return (
    <>
      <div
        style={{
          borderRadius: 16,
          padding: `${spacing.sm}px ${spacing.md}px`,
          border: '1px solid #e2e8f0'
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 0',
              borderBottom: '1px solid #e2e8f0',
              fontSize: fontSize.base
            }}
          >
            <span style={titleStyle}>Creado</span>
            <span style={infoStyle}>{creativity?.createdAt ? dayjs(creativity.createdAt).fromNow() : '-'}</span>
          </div>

          {creativity?.updatedAt && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: `${spacing.sm}px 0`,
                borderBottom: '1px solid #e2e8f0',
                fontSize: fontSize.base
              }}
            >
              <span style={titleStyle}>Actualizado</span>
              <span style={infoStyle}>{dayjs(creativity.updatedAt).fromNow()}</span>
            </div>
          )}

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: `${spacing.sm}px 0`,
              borderBottom: '1px solid #e2e8f0',
              fontSize: fontSize.base
            }}
          >
            <span style={titleStyle}>Estado</span>
            <span style={infoStyle}>{statusData?.message || '-'}</span>
          </div>

          {/* Botón de eliminar */}
          <div
            style={{
              paddingTop: spacing.md,
              display: 'flex',
              justifyContent: 'center',
              gap: spacing.sm
            }}
          >
            {editorRoute && (
              <ActionButton variant='secondary' fullWidth onClick={handleEdit}>
                <Pencil size={18} />
                Editar pieza
              </ActionButton>
            )}

            <ActionButton variant='outline' fullWidth onClick={() => setShowDeleteModal(true)}>
              <Trash2 size={18} />
              Eliminar
            </ActionButton>
          </div>
        </div>
      </div>

      {/* Modal de confirmación personalizado */}
      <Modal
        open={showDeleteModal}
        onCancel={() => !isDeleting && setShowDeleteModal(false)}
        footer={null}
        centered
        closable={!isDeleting}
        width={screens.xs ? 320 : screens.sm ? 360 : 400}
        styles={{
          body: { padding: 0 },
          mask: { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
          content: { padding: 0 }
        }}
      >
        <div
          style={{
            padding: screens.xs ? spacing.md : spacing.lg,
            textAlign: 'center'
          }}
        >
          {/* Título */}
          <Typography.Title
            level={4}
            style={{
              margin: `0 0 ${spacing.sm}px 0`,
              fontSize: fontSize.xl,
              fontWeight: 700,
              color: '#0f172a'
            }}
          >
            ¿Eliminar este asset?
          </Typography.Title>

          {/* Descripción */}
          <Typography.Text
            style={{
              display: 'block',
              marginBottom: spacing.md,
              fontSize: fontSize.base,
              color: '#64748b',
              lineHeight: 1.6
            }}
          >
            Esta acción es permanente y no se puede deshacer.
          </Typography.Text>

          {/* Botones */}
          <div
            style={{
              display: 'flex',
              flexDirection: screens.xs ? 'column' : 'row',
              gap: spacing.sm,
              justifyContent: 'center'
            }}
          >
            <ActionButton
              variant='outline'
              onClick={() => setShowDeleteModal(false)}
              disabled={isDeleting}
              fullWidth={screens.xs}
              style={{ flex: screens.xs ? 'none' : 1 }}
            >
              Cancelar
            </ActionButton>
            <ActionButton
              variant='primary'
              onClick={handleDelete}
              disabled={isDeleting}
              fullWidth={screens.xs}
              style={{ flex: screens.xs ? 'none' : 1 }}
            >
              {isDeleting ? <Spinner size={16} /> : 'Sí, eliminar'}
            </ActionButton>
          </div>
        </div>
      </Modal>
    </>
  );
};
